import React, { useEffect, useRef, useState } from 'react';
import styles from './UserModal.module.css';
import { getDisplayNameFromEmail } from '../../utils/userUtils';
import UserProfileServices from '../../services/userProfile.services';
import logger from '../../utils/logger';

const UserModal = ({ user, isOpen, onClose, variant = 'default', birthdayTiming = 'upcoming', rank = null }) => {
    logger.log('UserModal - received props:', { user: user?.username, rank, variant });
    
    const modalContentRef = useRef(null);
    const [isCurrentUser, setIsCurrentUser] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });

    // Show notification helper
    const showNotification = (type, title, message) => {
        setNotification({ show: true, type, title, message });
        setTimeout(() => {
            setNotification(prev => ({ ...prev, show: false }));
        }, 4000);
    };

    // Fetch current user for comparison
    useEffect(() => {
        if (isOpen && user) {
            const fetchCurrentUser = async () => {
                try {
                    const currentUserData = await UserProfileServices.getCurrentUser();
                    // Check if the modal is for the current user
                    const userEmail = user.email || user.username;
                    const currentUserEmail = currentUserData.email || currentUserData.username;
                    setIsCurrentUser(userEmail === currentUserEmail);
                } catch (error) {
                    logger.error('Error fetching current user:', error);
                }
            };
            fetchCurrentUser();
        }
    }, [isOpen, user]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleModalClickOutside = (event) => {
            if (isOpen && 
                modalContentRef.current && 
                !modalContentRef.current.contains(event.target)) {
                onClose();
            }
        };
        
        if (isOpen) {
            document.addEventListener('mousedown', handleModalClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleModalClickOutside);
        };
    }, [isOpen, onClose]);

    // Close modal with escape key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Format date to a more user-friendly format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const options = { 
                month: 'long', 
                day: 'numeric'
                // year removed as requested
            };
            
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; // Return original if invalid date
            
            return date.toLocaleDateString('en-US', options);
        } catch (error) {
            logger.error('Error formatting date:', error);
            return dateString; // Return original on error
        }
    };

    // Get initials from name or email (First name + Surname format)
    const getInitials = () => {
        const displayName = user.preferredName || user.displayName || getDisplayNameFromEmail(user.email || user.username);
        if (!displayName) return 'UN';
        
        const nameParts = displayName.trim().split(' ');
        if (nameParts.length >= 2) {
            // First name + last name initials
            return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
        } else {
            // If only one name, use first two characters or duplicate first character
            const singleName = nameParts[0];
            return singleName.length >= 2 
                ? (singleName.charAt(0) + singleName.charAt(1)).toUpperCase()
                : (singleName.charAt(0) + singleName.charAt(0)).toUpperCase();
        }
    };

    // Get achievement message based on user activities
    const getAchievementMessage = () => {
        if (variant === 'birthday') return null;
        
        if (isCurrentUser) {
            // Personalized messages for current user
            if (rank === 1) {
                return "🏆 Congratulations! You're the #1 star performer! Your dedication and hard work with the GSD platform is truly inspiring. Keep up the fantastic work! 🌟";
            } else if (rank === 2) {
                return "🥈 Excellent work! You've secured the 2nd position with your outstanding engagement on the GSD platform. You're so close to the top - keep pushing! 💪";
            } else if (rank === 3) {
                return "🥉 Well done! You've earned the 3rd spot through your consistent use of the GSD platform. Your efforts are recognized and appreciated! 👏";
            } else if (user.totalCount > 0) {
                return `✨ You've been making great progress with ${user.totalCount} activities on the GSD platform! Every action you take helps drive our team's success. Keep it up! 🚀`;
            }
        } else {
            // Messages for other users
            if (rank === 1) {
                return "🏆 This superstar has claimed the #1 spot! Their exceptional engagement with the GSD platform sets the bar high for everyone. Truly impressive work! 🌟";
            } else if (rank === 2) {
                return "🥈 A fantastic 2nd place achievement! This team member's dedication to using the GSD platform effectively is clearly paying off. Outstanding effort! 💪";
            } else if (rank === 3) {
                return "🥉 Securing 3rd place is no small feat! This colleague's consistent engagement with the GSD platform demonstrates real commitment to excellence. Kudos! 👏";
            } else if (user.totalCount > 0) {
                return `✨ This team member has logged ${user.totalCount} activities on the GSD platform, showing great dedication to our shared goals. Their contributions make a difference! 🚀`;
            }
        }
        
        return "🌟 This team member is making valuable contributions to our platform! Every interaction helps drive our collective success.";
    };

    // Get birthday timing message with personalization
    const getBirthdayMessage = () => {
        if (!isBirthday) return null;
        
        const baseMessages = {
            today: isCurrentUser 
                ? "🎉 HAPPY BIRTHDAY TO YOU! 🎉\nIt's YOUR special day! We hope you have an absolutely wonderful birthday filled with joy, laughter, and cake! 🎂✨"
                : "🎉 IT'S THE BIG DAY! 🎉\nToday's the day to celebrate! Send your warmest birthday wishes and make their day extra special! 🎂✨",
            thisWeek: isCurrentUser
                ? "🎈 Hope your birthday week was amazing! 🎈\nYour special day was earlier this week - we hope it was filled with joy and celebration! The birthday vibes continue! 🥳"
                : "🎈 Birthday Week Celebration! 🎈\nTheir special day was earlier this week, but the party continues! It's never too late to spread some birthday joy! 🥳",
            upcoming: isCurrentUser
                ? "🗓️ Someone's excited for their birthday! 🐦\nLooks like someone's getting ahead of the game with early wishes! Thank you for the advance birthday love! 😄"
                : "🗓️ Early Bird Alert! 🐦\nLooks like someone's getting ahead of the game! You can wish them now for extra brownie points, or wait for the big day - your choice! 😄"
        };
        
        return baseMessages[birthdayTiming] || null;
    };

    if (!isOpen || !user) {
        return null;
    }

    // Determine if this is a birthday variant and apply appropriate styling
    const isBirthday = variant === 'birthday';
    let modalClass = styles.userModal;
    
    if (isBirthday) {
        modalClass += ` ${styles.birthdayModal}`;
        // Add specific timing classes for different effects
        if (birthdayTiming === 'today') {
            modalClass += ` ${styles.birthdayToday}`;
        } else if (birthdayTiming === 'thisWeek') {
            modalClass += ` ${styles.birthdayThisWeek}`;
        } else {
            modalClass += ` ${styles.birthdayUpcoming}`;
        }
    } else if (rank) {
        // Add ranking-specific styling for leaderboard modals
        if (rank === 1) {
            modalClass += ` ${styles.firstPlace}`;
        } else if (rank === 2) {
            modalClass += ` ${styles.secondPlace}`;
        } else if (rank === 3) {
            modalClass += ` ${styles.thirdPlace}`;
        }
    }

    return (
        <>
            <div className={modalClass}>
                <div className={styles.modalInner} ref={modalContentRef}>
                    {rank && (
                        <div className={styles.rankBadge}>
                            {rank === 1 && <span className={styles.rankFirst}>🥇 #1</span>}
                            {rank === 2 && <span className={styles.rankSecond}>🥈 #2</span>}
                            {rank === 3 && <span className={styles.rankThird}>🥉 #3</span>}
                        </div>
                    )}
                    <button 
                        className={styles.modalClose} 
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                <div className={styles.modalContent}>
                    <div className={styles.avatarContainer}>
                        {user.profilePictureUrl ? (
                            <img
                                src={user.profilePictureUrl}
                                alt={user.preferredName || user.displayName || getDisplayNameFromEmail(user.email)}
                                className={styles.modalAvatar}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    // Replace with initials fallback
                                    const initialsDiv = document.createElement('div');
                                    initialsDiv.className = styles.initialsAvatar;
                                    initialsDiv.textContent = getInitials();
                                    e.target.parentNode.replaceChild(initialsDiv, e.target);
                                }}
                            />
                        ) : (
                            <div className={styles.initialsAvatar}>
                                {getInitials()}
                            </div>
                        )}
                    </div>
                    <h2 className={styles.userNameTitle}>
                        {user.preferredName || user.displayName || getDisplayNameFromEmail(user.email)}
                    </h2>
                    {user.groups && user.groups.length > 0 && (
                        <p className={styles.userRole}>{user.groups.join(' • ')}</p>
                    )}
                    <div className={styles.modalDetails}>
                        <p><strong>Email:</strong> {user.email || user.username}</p>
                        <p><strong>Groups:</strong> {user.groups ? user.groups.join(', ') : 'N/A'}</p>
                        <p><strong>Location:</strong> {user.city || 'N/A'}</p>
                        {user.birthday && (
                            <p><strong>Birthday:</strong> {formatDate(user.birthday)}</p>
                        )}
                        {user.totalCount && (
                            <p><strong>Total Activities:</strong> {user.totalCount}</p>
                        )}
                        {user.activity && (
                            <p><strong>Activities:</strong> {user.activity}</p>
                        )}
                    </div>
                    {(isBirthday && getBirthdayMessage()) && (
                        <div className={styles.birthdayMessage}>
                            {getBirthdayMessage().split('\n').map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </div>
                    )}
                    {(!isBirthday && getAchievementMessage()) && (
                        <div className={styles.achievementMessage}>
                            {getAchievementMessage()}
                        </div>
                    )}
                    <div className={styles.actionButtons}>
                        {isBirthday ? (
                            <>
                                {!isCurrentUser && (
                                    <button
                                        className={styles.actionButton}
                                        onClick={() => window.open('https://meltwater.slack.com/archives/C01AGPDN5GU', '_blank')}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ marginRight: '8px' }}>
                                            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                                        </svg>
                                        Send Birthday Message
                                    </button>
                                )}
                                <button 
                                    className={styles.actionButton}
                                    onClick={() => window.open('https://bonus.ly/', '_blank')}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ marginRight: '8px' }}>
                                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                    </svg>
                                    Give Bonusly Kudos
                                </button>
                            </>
                        ) : (
                            <button
                                className={styles.actionButton}
                                onClick={() => window.open('https://bonus.ly/', '_blank')}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ marginRight: '8px' }}>
                                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                </svg>
                                Give Bonusly Kudos
                            </button>
                        )}
                    </div>
                    <div className={styles.actionButtons} style={{ marginTop: '8px' }}>
                        <button 
                            className={styles.iconButton}
                            onClick={() => window.open(`mailto:${user.email || user.username}?subject=${isBirthday ? 'Happy Birthday!' : 'Great work!'}`, '_blank')}
                            title="Send Email"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                            </svg>
                        </button>
                        {isCurrentUser && (
                            <button 
                                className={styles.iconButton}
                                onClick={() => {
                                    let message = '';
                                    
                                    if (isBirthday) {
                                        message = `🎉 It's my birthday and I'm celebrating with the amazing GSD team! Thank you for all the birthday wishes! 🎂✨`;
                                    } else {
                                        const rankText = rank ? ` I ranked #${rank} and` : '';
                                        message = `🎉 Check out my achievements on GSD!${rankText} ${getAchievementMessage().replace(/🏆|🥈|🥉|✨|🌟|💪|👏|🚀/g, '').trim()}`;
                                    }
                                    
                                    // Copy to clipboard and show graceful notification
                                    navigator.clipboard.writeText(message).then(() => {
                                        showNotification(
                                            'success', 
                                            '🎉 Message Copied!', 
                                            'Your announcement message has been copied to clipboard. Ready to paste in Slack!'
                                        );
                                    }).catch(() => {
                                        // Fallback for older browsers
                                        try {
                                            const textArea = document.createElement('textarea');
                                            textArea.value = message;
                                            document.body.appendChild(textArea);
                                            textArea.select();
                                            document.execCommand('copy');
                                            document.body.removeChild(textArea);
                                            showNotification(
                                                'success', 
                                                '📢 Message Copied!', 
                                                'Your announcement message has been copied to clipboard using fallback method.'
                                            );
                                        } catch (fallbackError) {
                                            showNotification(
                                                'error', 
                                                '❌ Copy Failed', 
                                                'Unable to copy to clipboard. Please manually copy your achievement message.'
                                            );
                                        }
                                    });
                                }}
                                title="Announce on Slack"
                            >
                                📢
                            </button>
                        )}
                    </div>
                </div>
            </div>
            </div>
            
            {/* Notification Component */}
            {notification.show && (
                <div className={`${styles.notification} ${styles[notification.type]} ${notification.show ? styles.show : styles.hide}`}>
                    <div className={styles.notificationIcon}>
                        {notification.type === 'success' && '✅'}
                        {notification.type === 'error' && '❌'}
                        {notification.type === 'info' && 'ℹ️'}
                    </div>
                    <div className={styles.notificationContent}>
                        <h4>{notification.title}</h4>
                        <p>{notification.message}</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserModal;
